import { memo } from 'react';

export interface LogoProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show text alongside logo */
  showText?: boolean;
  /** Additional class names */
  className?: string;
  /** Text class for gradient styling */
  textClassName?: string;
}

const SIZES = {
  sm: { img: 'h-6 w-6', text: 'text-sm' },
  md: { img: 'h-8 w-8', text: 'text-lg' },
  lg: { img: 'h-9 w-9', text: 'text-xl' },
} as const;

/**
 * Logo component with proper image attributes
 *
 * Features:
 * - Empty alt for decorative usage (text provides context)
 * - aria-hidden when used with visible text
 * - Proper width/height to prevent layout shift
 * - Lazy loading for off-screen logos
 */
export const Logo = memo(function Logo({
  size = 'md',
  showText = true,
  className = '',
  textClassName = 'bg-gradient-to-r from-blue-400 to-gold-500 bg-clip-text text-transparent',
}: LogoProps) {
  const sizeConfig = SIZES[size];
  const imgSize = size === 'sm' ? 24 : size === 'md' ? 32 : 36;

  return (
    <span className={`flex items-center gap-2 ${className}`}>
      <img
        src="/writerslogic-icon.svg"
        alt=""
        width={imgSize}
        height={imgSize}
        className={sizeConfig.img}
        loading="lazy"
        decoding="async"
        aria-hidden="true"
      />
      {showText && (
        <span
          className={`font-bold ${sizeConfig.text} ${textClassName}`}
          // Text is not hidden - it provides the accessible name
        >
          WritersLogic
        </span>
      )}
    </span>
  );
});

/**
 * Compact logo for footer and small spaces
 */
export const LogoCompact = memo(function LogoCompact({
  className = '',
}: {
  className?: string;
}) {
  return (
    <span className={`flex items-center gap-2 ${className}`}>
      <img
        src="/writerslogic-icon.svg"
        alt=""
        width={28}
        height={28}
        className="h-7 w-7"
        loading="lazy"
        decoding="async"
        aria-hidden="true"
      />
      <span className="text-gray-400">WritersLogic</span>
    </span>
  );
});

export default Logo;
